import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getOnboardingSession, getOnboardingUrl, enableGiving, simulateApplicationStatus } from '@/data/giving';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import Toast from 'react-native-toast-message';
import Clipboard from '@react-native-clipboard/clipboard';
import GivingStepper from '@/components/Giving/GivingStepper';
import GivingOnboardingWebView from '@/components/Giving/GivingOnboardingWebView';

// Sandbox gate: non-prod Supabase project = Rainforest sandbox. The prod
// project ref is mlmibpzqkvhttynlzgpw; staging/local .env.local points elsewhere.
// Dev-only simulate controls must NEVER render in production.
const isSandbox = (process.env.EXPO_PUBLIC_DB_URL || '') !== 'https://mlmibpzqkvhttynlzgpw.supabase.co';

const PENDING_STATUSES = ['PROCESSING', 'IN_REVIEW'];
const REVIEW_STATUSES = ['IN_REVIEW'];
const APPROVED_STATUSES = ['COMPLETED', 'ACTIVE'];
const DECLINED_STATUSES = ['DECLINED', 'CANCELED'];

export default function GivingOnboardingScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { theme: { colors, scheme } } = useUnistyles();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID });
    const { data: givingMerchant, isFetching: loadingMerchant } = Query.giving.merchant({ organizationID: loggedInUser?.organizationID });

    const organizationID = organization?.id;
    const [session, setSession] = useState(null);
    const [loadingSession, setLoadingSession] = useState(false);

    const status = useMemo(() => (givingMerchant?.applicationStatus || givingMerchant?.merchantStatus || '').toUpperCase(), [givingMerchant]);

    // The merchant row stays at its pre-submit status until the Rainforest
    // webhook lands; latch 'PROCESSING' after the component reports submission
    // so the stale row can't drop the user back onto the form's review step.
    const [submitted, setSubmitted] = useState(false);
    const displayStatus = useMemo(() => {
        if (submitted &&
            !APPROVED_STATUSES.includes(status) &&
            !DECLINED_STATUSES.includes(status) &&
            !PENDING_STATUSES.includes(status) &&
            status !== 'NEEDS_INFORMATION') {
            return 'PROCESSING';
        }
        return status;
    }, [submitted, status]);

    const enableMutation = useMutation({
        mutationFn: () => enableGiving(organizationID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
            Toast.show({ type: 'success', text2: 'Re-application started' });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to re-apply' });
        },
    });

    const simulateMutation = useMutation({
        mutationFn: (simulateStatus) => simulateApplicationStatus({ organizationID, status: simulateStatus }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
            Toast.show({ type: 'success', text2: 'Simulation submitted — status updates via webhook' });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Simulation failed' });
        },
    });

    useEffect(() => {
        if (!organizationID) return;
        setLoadingSession(true);
        getOnboardingSession(organizationID)
            .then(setSession)
            .catch((error) => Toast.show({ type: 'error', text2: error?.message || 'Failed to load onboarding session' }))
            .finally(() => setLoadingSession(false));
    }, [organizationID]);

    const url = useMemo(() => {
        if (!session) return null;
        return getOnboardingUrl({
            sessionKey: session.sessionKey,
            merchantID: session.merchantID,
            merchantApplicationID: session.merchantApplicationID,
            termsUrl: session.termsUrl,
            theme: scheme === 'dark' ? 'dark' : 'light',
            bg: colors.surface,
        });
    }, [session, scheme]);

    const handleMessage = (data) => {
        if (data?.type === 'rainforest_onboarding_submitted') {
            setSubmitted(true);
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
            Toast.show({ type: 'success', text2: 'Application submitted for review' });
        }
    };

    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
    };

    const handleCopyUrl = () => {
        const publicUrl = `${process.env.EXPO_PUBLIC_APP_WEB_URL || 'https://app.reachchms.com'}/give/${organization?.handle}`;
        Clipboard.setString(publicUrl);
        Toast.show({ type: 'success', text2: 'Public giving URL copied' });
    };

    const isLoading = loadingMerchant || loadingSession;

    const renderContent = () => {
        if (isLoading) {
            return (
                <View style={styles.center} testID="giving-onboarding-loading">
                    <ActivityIndicator color={colors.giving} />
                </View>
            );
        }

        if (APPROVED_STATUSES.includes(displayStatus)) {
            return (
                <View style={styles.statusCard} testID="giving-approved-card">
                    <View style={styles.statusIconContainer}>
                        <Unicon name="checkmark-circle" size={40} colorVariant="success" />
                    </View>
                    <Text style={styles.statusHeading}>You&apos;re ready to give!</Text>
                    <Text style={styles.statusBody}>
                        Your application was approved. You can start giving or share your public giving page.
                    </Text>
                    <SolidButton
                        testID="open-give-screen-button"
                        text="Open Give"
                        iconName="heart-outline"
                        onPress={() => router.push('/give')}
                    />
                    <SolidButton
                        testID="approved-copy-url-button"
                        text="Copy public URL"
                        variant="outlined"
                        iconName="copy-outline"
                        onPress={handleCopyUrl}
                    />
                </View>
            );
        }

        if (DECLINED_STATUSES.includes(displayStatus)) {
            return (
                <View style={styles.statusCard} testID="giving-declined-card">
                    <View style={styles.statusIconContainer}>
                        <Unicon name="close-circle" size={40} colorVariant="error" />
                    </View>
                    <Text style={styles.statusHeading}>Application declined</Text>
                    <Text style={styles.statusBody}>
                        Your application was not approved. You can review your details and re-apply.
                    </Text>
                    <SolidButton
                        testID="reapply-giving-button"
                        text="Re-apply"
                        iconName="refresh-outline"
                        processing={enableMutation.isPending}
                        onPress={() => enableMutation.mutate()}
                    />
                </View>
            );
        }

        if (PENDING_STATUSES.includes(displayStatus)) {
            const isReviewing = REVIEW_STATUSES.includes(displayStatus);
            return (
                <View style={styles.statusCard} testID="giving-pending-card">
                    <View style={styles.statusIconContainer}>
                        <Unicon name="time-outline" size={40} colorVariant="giving" />
                    </View>
                    <Text style={styles.statusHeading}>{isReviewing ? 'Under review' : 'Processing'}</Text>
                    <Text style={styles.statusBody}>
                        {isReviewing
                            ? "Your application is being manually reviewed. We'll notify you when the status changes."
                            : "Your application has been submitted and is being processed. We'll notify you when the status changes."}
                    </Text>
                    <SolidButton
                        testID="refresh-giving-status-button"
                        text="Refresh status"
                        variant="outlined"
                        iconName="refresh-outline"
                        onPress={handleRefresh}
                    />
                    {isSandbox && (
                        <View style={styles.devSection} testID="sandbox-simulate-section">
                            <Text style={styles.devLabel}>Sandbox: simulate decision</Text>
                            <View style={styles.devButtonsRow}>
                                <SolidButton
                                    testID="sandbox-simulate-approve"
                                    text="Approve"
                                    variant="outlined"
                                    type="secondary"
                                    size="sm"
                                    disabled={simulateMutation.isPending}
                                    onPress={() => simulateMutation.mutate('COMPLETED')}
                                />
                                <SolidButton
                                    testID="sandbox-simulate-decline"
                                    text="Decline"
                                    variant="outlined"
                                    type="secondary"
                                    size="sm"
                                    disabled={simulateMutation.isPending}
                                    onPress={() => simulateMutation.mutate('DECLINED')}
                                />
                                <SolidButton
                                    testID="sandbox-simulate-needs-info"
                                    text="Needs info"
                                    variant="outlined"
                                    type="secondary"
                                    size="sm"
                                    disabled={simulateMutation.isPending}
                                    onPress={() => simulateMutation.mutate('NEEDS_INFORMATION')}
                                />
                            </View>
                        </View>
                    )}
                </View>
            );
        }

        return (
            <View style={styles.webviewCard} testID="giving-onboarding-webview-card">
                {url ? (
                    <GivingOnboardingWebView url={url} onMessage={handleMessage} />
                ) : (
                    <View style={styles.center}>
                        <ActivityIndicator color={colors.giving} />
                    </View>
                )}
            </View>
        );
    };

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
            <GivingStepper status={displayStatus} />
            {renderContent()}
        </ScrollView>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        flex: 1,
        padding: spacing.md,
        gap: spacing.md,
        _web: {
            maxWidth: 800,
            alignSelf: 'center',
            width: '100%',
        },
    },
    center: {
        padding: spacing.xl,
        alignItems: 'center',
        justifyContent: 'center',
    },
    webviewCard: {
        minHeight: 600,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
    },
    statusCard: {
        borderRadius: 12,
        padding: spacing.lg,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        alignItems: 'center',
        gap: spacing.md,
    },
    statusIconContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.givingContainer,
    },
    statusHeading: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    statusBody: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: 22,
    },
    devSection: {
        alignItems: 'center',
        gap: spacing.xs,
        marginTop: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.textSecondaryContainer,
        alignSelf: 'stretch',
    },
    devLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    devButtonsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: spacing.xs,
    },
}));
